import { useBranchLogo } from '@/entities/Branch';
import { useFormBuilderToast } from '../composables/useFormBuilderToast';
import { watchDebounced } from '@vueuse/core';
import { defineStore } from 'pinia';
import { computed, readonly, ref } from 'vue';
import { z } from 'zod';
import type { PageOrientation } from '@record-fill';
import { generateElementId } from '../lib/create-element';
import {
  DEFAULT_FIRST_ELEMENT_AFTER_HEADER_MARGIN_TOP,
  DEFAULT_HEADER_MAIN_LABEL,
  DEFAULT_HEADER_MARGIN,
  DEFAULT_SPACING,
} from '../lib/element-defaults';
import { iterateFormElements } from '../lib/form-element-traversal';
import { migrateLoadedElements } from '../lib/migrate-loaded-elements';
import { createHeaderElement } from '../lib/useHeaderTableFactory';
import type {
  ElementType,
  FormElement,
  FormStructure,
  HeaderProperties,
  TableProperties,
} from './form.types';
import { useTableEditorStore } from './table-editor.store';
import { clonePlain, useLimitedHistory } from './useLimitedHistory';

const STORAGE_VERSION = 1;

const ELEMENT_TYPES: ElementType[] = [
  'HEADER',
  'FIXED_TEXT',
  'TEXT_INPUT',
  'PARAGRAPH',
  'NUMBER_INPUT',
  'DATE_INPUT',
  'URL_INPUT',
  'EMAIL_INPUT',
  'REGISTER_NUMBER_INPUT',
  'INDEXER_TITLE',
  'TABLE',
  'IMAGE',
  'DROPDOWN',
  'RADIO_GROUP',
  'CHECKBOX',
];

const formElementSchema = z.object({
  id: z.string(),
  type: z.enum(ELEMENT_TYPES as [ElementType, ...ElementType[]]),
  properties: z
    .object({
      name: z.string(),
    })
    .catchall(z.unknown()),
  position: z.number(),
});

const storedDraftSchema = z.object({
  version: z.number(),
  elements: z.array(formElementSchema),
  orientation: z.enum(['portrait', 'landscape']).optional(),
});

interface StoredDraft {
  version: number;
  elements: FormElement[];
  orientation: PageOrientation;
}

interface LoadedDraft {
  elements: FormElement[];
  orientation: PageOrientation;
}

interface FormBuilderHistorySnapshot {
  elements: FormElement[];
  canvasVersion: string;
}

interface UpdateElementOptions {
  trackHistory?: boolean;
}

interface LoadFormStructureOptions {
  preferStoredDraft?: boolean;
}

function getStorageKey(branchId: string | null): string | null {
  if (!branchId) return null;
  return `form-builder-draft-branch-${branchId}`;
}

function getEditStorageKey(id: number, kind: 'matrix' | 'model' = 'matrix'): string {
  return `form-builder-draft-${kind}-${id}`;
}

function applyDefaultMarginForFirstElementAfterHeader(
  element: FormElement,
  previousElement?: FormElement
): FormElement {
  if (previousElement?.type !== 'HEADER' || element.type === 'TABLE') {
    return element;
  }

  const currentMargin = element.properties.margin;
  const currentTopMargin = currentMargin?.top;
  const hasCustomTopMargin =
    currentTopMargin !== undefined && currentTopMargin !== DEFAULT_SPACING.margin.top;

  if (hasCustomTopMargin) {
    return element;
  }

  return {
    ...element,
    properties: {
      ...element.properties,
      autoHeaderSpacing: true,
      margin: {
        top: DEFAULT_FIRST_ELEMENT_AFTER_HEADER_MARGIN_TOP,
        right: currentMargin?.right ?? DEFAULT_SPACING.margin.right,
        bottom: currentMargin?.bottom ?? DEFAULT_SPACING.margin.bottom,
        left: currentMargin?.left ?? DEFAULT_SPACING.margin.left,
      },
    },
  };
}

function refreshHeaderLogoOnLoadedElements(
  elements: FormElement[],
  currentLogoUrl: string
): FormElement[] {
  return elements.map((element) => {
    if (element.type !== 'TABLE') {
      return element;
    }

    const tableProperties = element.properties as TableProperties;
    if (tableProperties.isHeader !== true) {
      return element;
    }

    const cells = tableProperties.cells.map((row, rowIndex) =>
      row.map((cell, colIndex) => {
        const content = cell.content;
        if (!content || content.type !== 'IMAGE') {
          return cell;
        }

        const imageName = content.properties.name ?? '';
        const isHeaderLogoCell = rowIndex === 0 && colIndex === 0;
        const isHeaderLogoByName = imageName.startsWith('logo_');

        if (!isHeaderLogoCell && !isHeaderLogoByName) {
          return cell;
        }

        return {
          ...cell,
          content: {
            ...content,
            properties: {
              ...content.properties,
              imageUrl: currentLogoUrl,
            },
          },
        };
      })
    );

    return {
      ...element,
      properties: {
        ...tableProperties,
        cells,
      },
    };
  });
}

function regenerateElementName(name: string, previousId: string, newShortId: string): string;
function regenerateElementName(
  name: string | undefined,
  previousId: string,
  newShortId: string
): string | undefined;
function regenerateElementName(
  name: string | undefined,
  previousId: string,
  newShortId: string
): string | undefined {
  if (!name) return name;
  const previousShortId = previousId.slice(previousId.lastIndexOf('-') + 1);
  if (previousShortId && name.endsWith(previousShortId)) {
    return `${name.slice(0, name.length - previousShortId.length)}${newShortId}`;
  }
  return `${name}_${newShortId}`;
}

function regenerateElementIds(element: FormElement): FormElement {
  const { elementId, shortId } = generateElementId(element.type);
  const name = regenerateElementName(element.properties.name, element.id, shortId);

  if (element.type === 'TABLE') {
    const tableProperties = element.properties as TableProperties;
    const cells = tableProperties.cells.map((row) =>
      row.map((cell) => {
        if (!cell.content) return cell;
        const { elementId: contentId, shortId: contentShortId } = generateElementId(
          cell.content.type
        );
        return {
          ...cell,
          content: {
            ...cell.content,
            id: contentId,
            properties: {
              ...cell.content.properties,
              name: regenerateElementName(
                cell.content.properties.name,
                cell.content.id,
                contentShortId
              ),
            },
          },
        };
      })
    );
    const properties: TableProperties = { ...tableProperties, cells, name };
    return { ...element, id: elementId, properties };
  }

  return { ...element, id: elementId, properties: { ...element.properties, name } };
}

function loadFromStorage(key: string | null): LoadedDraft {
  const empty: LoadedDraft = { elements: [], orientation: 'portrait' };
  if (!key) return empty;
  try {
    const stored = localStorage.getItem(key);
    let raw: FormElement[] = [];
    let orientation: PageOrientation = 'portrait';
    if (stored) {
      const parsed = JSON.parse(stored);

      if (Array.isArray(parsed)) {
        const result = z.array(formElementSchema).safeParse(parsed);
        if (result.success) {
          raw = result.data as FormElement[];
        } else {
          localStorage.removeItem(key);
          return empty;
        }
      } else {
        const result = storedDraftSchema.safeParse(parsed);
        if (result.success && result.data.version === STORAGE_VERSION) {
          raw = result.data.elements as FormElement[];
          orientation = result.data.orientation ?? 'portrait';
        } else {
          localStorage.removeItem(key);
          return empty;
        }
      }
    }
    return { elements: migrateLoadedElements(raw), orientation };
  } catch {
    localStorage.removeItem(key);
  }
  return empty;
}

function ensureHeaderExists(elements: FormElement[]): FormElement[] {
  if (elements.some((el) => el.type === 'HEADER')) return elements;
  const shortId = crypto.randomUUID().slice(0, 8);
  const header = createHeaderElement(shortId);
  return [{ ...header, position: 0 }, ...elements.map((el, i) => ({ ...el, position: i + 1 }))];
}

function saveToStorage(
  elements: FormElement[],
  orientation: PageOrientation,
  key: string | null,
  notifyQuotaExceeded: () => void
) {
  if (!key) return;
  try {
    const data: StoredDraft = { version: STORAGE_VERSION, elements, orientation };
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    if (error instanceof DOMException && error.name === 'QuotaExceededError') {
      notifyQuotaExceeded();
    }
  }
}

export const useFormBuilderStore = defineStore('form-builder', () => {
  const { currentLogoUrl, refreshCurrentLogo } = useBranchLogo();
  refreshCurrentLogo();

  const currentBranchId = ref<string | null>(localStorage.getItem('selectedBranchId'));
  const localStorageSuspended = ref(false);
  const editDraftStorageKey = ref<string | null>(null);

  const elements = ref<FormElement[]>(ensureHeaderExists([]));
  const selectedElementId = ref<string | null>(null);
  const selectedHeaderPageIndex = ref<number | null>(null);
  const canvasVersion = ref('1.0');
  const pageOrientation = ref<PageOrientation>('portrait');
  const propertyEditorValidity = ref<Record<string, boolean>>({});
  const { showWarningToast } = useFormBuilderToast();

  function createHistorySnapshot(): FormBuilderHistorySnapshot {
    return {
      elements: clonePlain(elements.value),
      canvasVersion: canvasVersion.value,
    };
  }

  function restoreHistorySnapshot(snapshot: FormBuilderHistorySnapshot) {
    elements.value = ensureHeaderExists(clonePlain(snapshot.elements));
    canvasVersion.value = snapshot.canvasVersion;
    selectedElementId.value = null;
    selectedHeaderPageIndex.value = null;

    const tableEditorStore = useTableEditorStore();
    if (tableEditorStore.isEditingMode) {
      tableEditorStore.exitEditingMode();
    }
  }

  const history = useLimitedHistory<FormBuilderHistorySnapshot>({
    capture: createHistorySnapshot,
    restore: restoreHistorySnapshot,
  });

  const canUndo = history.canUndo;
  const canRedo = history.canRedo;

  const notifyQuotaExceeded = () => {
    showWarningToast({
      title:
        'Não foi possível salvar o rascunho automaticamente. Considere salvar o formulário.',
      type: 'warning',
      hideIcon: true,
    });
  };

  watchDebounced(
    [elements, pageOrientation],
    ([newElements, newOrientation]) => {
      if (localStorageSuspended.value) return;
      saveToStorage(newElements, newOrientation, getActiveStorageKey(), notifyQuotaExceeded);
    },
    { deep: true, debounce: 500 }
  );

  const selectedElement = computed(() => {
    if (!selectedElementId.value) return null;
    return elements.value.find((el) => el.id === selectedElementId.value) ?? null;
  });

  const hasElements = computed(() => elements.value.length > 0);

  const isHeaderModified = computed(() => {
    const header = elements.value.find((el) => el.type === 'HEADER');
    if (!header) return false;
    const props = header.properties as HeaderProperties;
    const defaultMargin = { 0: { ...DEFAULT_HEADER_MARGIN } };
    const defaultMarginStr = JSON.stringify(defaultMargin);
    if ((props.mainLabel ?? DEFAULT_HEADER_MAIN_LABEL) !== DEFAULT_HEADER_MAIN_LABEL) return true;
    if (props.rq && props.rq !== '') return true;
    if (props.rev && props.rev !== '') return true;
    if (props.title && props.title !== '') return true;
    if (JSON.stringify(props.marginByPage ?? defaultMargin) !== defaultMarginStr) return true;
    return false;
  });

  const isHeaderComplete = computed(() => {
    const header = elements.value.find((el) => el.type === 'HEADER');
    if (!header) return false;
    const props = header.properties as HeaderProperties;
    return [props.rq, props.rev, props.title].every(
      (value) => typeof value === 'string' && value.trim().length > 0
    );
  });

  const hasEmptyFixedText = computed(() =>
    [...iterateFormElements(elements.value)].some(
      (el) => el.type === 'FIXED_TEXT' && (el.properties.content ?? '').trim().length === 0
    )
  );

  const hasInvalidPropertyEditor = computed(() =>
    Object.values(propertyEditorValidity.value).some((valid) => !valid)
  );

  function setPropertyEditorValidity(id: string, valid: boolean) {
    propertyEditorValidity.value[id] = valid;
  }

  function clearPropertyEditorValidity(id: string) {
    delete propertyEditorValidity.value[id];
  }

  const elementCount = computed(() => elements.value.length);

  const formStructure = computed(
    (): FormStructure => ({
      elements: elements.value,
      version: canvasVersion.value,
    })
  );

  function addElement(element: FormElement) {
    const previousElement = elements.value[elements.value.length - 1];
    const elementWithDefaultMargin = applyDefaultMarginForFirstElementAfterHeader(
      element,
      previousElement
    );
    const position = elements.value.length;
    history.track();
    elements.value.push({
      ...elementWithDefaultMargin,
      position,
    });
    selectedElementId.value = elementWithDefaultMargin.id;

    if (elementWithDefaultMargin.type === 'TABLE') {
      const tableEditorStore = useTableEditorStore();
      tableEditorStore.enterEditingMode(elementWithDefaultMargin.id);
    }
    if (elementWithDefaultMargin.type === 'HEADER') {
      selectedHeaderPageIndex.value = null;
    }
  }

  function removeElement(elementId: string) {
    const element = elements.value.find((el) => el.id === elementId);
    if (element?.type === 'HEADER') return;
    if (element?.type === 'TABLE' && (element.properties as TableProperties).isHeader === true)
      return;
    const index = elements.value.findIndex((el) => el.id === elementId);
    if (index !== -1) {
      const toRemove = elements.value[index];
      history.track();
      elements.value.splice(index, 1);
      updatePositions();

      if (selectedElementId.value === elementId) {
        selectedElementId.value = null;
        selectedHeaderPageIndex.value = null;
      }

      if (toRemove.type === 'TABLE') {
        const tableEditorStore = useTableEditorStore();
        if (tableEditorStore.activeTableId === elementId) {
          tableEditorStore.exitEditingMode();
        }
      }
    }
  }

  function insertElementAt(index: number, element: FormElement) {
    if (index < 0 || index > elements.value.length) {
      return;
    }
    history.track();
    elements.value.splice(index, 0, element);
    updatePositions();
  }

  function updateElement(
    elementId: string,
    updates: Partial<FormElement>,
    options: UpdateElementOptions = {}
  ) {
    const index = elements.value.findIndex((el) => el.id === elementId);
    if (index !== -1) {
      if (options.trackHistory !== false) {
        history.track();
      }
      elements.value[index] = {
        ...elements.value[index],
        ...updates,
      };
    }
  }

  function selectElement(elementId: string | null, headerPageIndex?: number) {
    const tableEditorStore = useTableEditorStore();

    if (!elementId) {
      if (tableEditorStore.isEditingMode) {
        tableEditorStore.exitEditingMode();
      }
      selectedElementId.value = null;
      selectedHeaderPageIndex.value = null;
      return;
    }

    selectedElementId.value = elementId;
    const element = elements.value.find((el) => el.id === elementId);

    if (element?.type === 'HEADER') {
      if (tableEditorStore.isEditingMode) {
        tableEditorStore.exitEditingMode();
      }
      selectedHeaderPageIndex.value = headerPageIndex ?? 0;
      return;
    }
    selectedHeaderPageIndex.value = null;

    const isEditableTable =
      element?.type === 'TABLE' && (element.properties as TableProperties).isHeader !== true;

    if (isEditableTable) {
      if (tableEditorStore.activeTableId !== elementId) {
        tableEditorStore.enterEditingMode(elementId);
      }
      return;
    }

    if (tableEditorStore.isEditingMode) {
      tableEditorStore.exitEditingMode();
    }
  }

  function moveElement(fromIndex: number, toIndex: number) {
    if (fromIndex < 0 || fromIndex >= elements.value.length) return;
    if (toIndex < 0 || toIndex >= elements.value.length) return;

    history.track();
    const [movedElement] = elements.value.splice(fromIndex, 1);
    elements.value.splice(toIndex, 0, movedElement);
    updatePositions();
  }

  // Callers such as the canvas drag hand back items read from the readonly public list, and a
  // write on a readonly proxy is silently dropped, so the positions go on new objects.
  function updatePositions() {
    elements.value = elements.value.map((element, index) => ({ ...element, position: index }));
  }

  function initForCreateMode() {
    currentBranchId.value = localStorage.getItem('selectedBranchId');
    localStorageSuspended.value = false;
    editDraftStorageKey.value = null;
    const loaded = loadFromStorage(getStorageKey(currentBranchId.value));
    pageOrientation.value = loaded.orientation;
    const migrated = migrateLoadedElements(loaded.elements);
    const withLogo = refreshHeaderLogoOnLoadedElements(migrated, currentLogoUrl.value);
    elements.value = ensureHeaderExists(withLogo);
    selectedElementId.value = null;
    selectedHeaderPageIndex.value = null;
    history.reset();
  }

  function getActiveStorageKey(): string | null {
    return editDraftStorageKey.value ?? getStorageKey(currentBranchId.value);
  }

  function loadFormStructure(
    structure: FormStructure,
    options: LoadFormStructureOptions = {}
  ) {
    refreshCurrentLogo();
    const storedDraft = options.preferStoredDraft
      ? loadFromStorage(getActiveStorageKey()).elements
      : [];
    const sourceElements = storedDraft.length > 0 ? storedDraft : structure.elements;
    const migrated = migrateLoadedElements(sourceElements);
    elements.value = ensureHeaderExists(
      refreshHeaderLogoOnLoadedElements(migrated, currentLogoUrl.value)
    );
    canvasVersion.value = structure.version;
    selectedElementId.value = null;
    selectedHeaderPageIndex.value = null;
    history.reset();
  }

  function appendModelStructure(modelElements: FormElement[]) {
    const migrated = migrateLoadedElements(modelElements);
    const appendable = migrated.filter(
      (element) =>
        element.type !== 'HEADER' &&
        !(element.type === 'TABLE' && (element.properties as TableProperties).isHeader === true)
    );

    for (const element of appendable) {
      const previousElement = elements.value[elements.value.length - 1];
      const withDefaultMargin = applyDefaultMarginForFirstElementAfterHeader(
        regenerateElementIds(element),
        previousElement
      );
      elements.value.push({ ...withDefaultMargin, position: elements.value.length });
    }
  }

  function clearCanvas(options: { keepRecordFields?: boolean } = {}) {
    history.track();
    const header = elements.value.find((el) => el.type === 'HEADER');
    if (header) {
      const shortId = header.id.replace(/^header-/, '');
      // In edit mode the canvas header locks RQ/REV, so the user could not retype them.
      const currentProperties = header.properties as HeaderProperties;
      const rq = options.keepRecordFields ? (currentProperties.rq ?? '') : '';
      const rev = options.keepRecordFields ? (currentProperties.rev ?? '') : '';
      const resetHeader: FormElement = {
        id: header.id,
        type: 'HEADER',
        position: 0,
        properties: {
          name: `header_${shortId}`,
          mainLabel: DEFAULT_HEADER_MAIN_LABEL,
          rq,
          rev,
          title: '',
          marginByPage: { 0: { ...DEFAULT_HEADER_MARGIN } },
        } as HeaderProperties,
      };
      elements.value = [resetHeader];
    } else {
      elements.value = ensureHeaderExists([]);
    }
    selectedElementId.value = null;
    selectedHeaderPageIndex.value = null;
    if (!localStorageSuspended.value) {
      const key = getActiveStorageKey();
      if (key) localStorage.removeItem(key);
    }
  }

  function clearDraft() {
    const key = getActiveStorageKey();
    if (key) localStorage.removeItem(key);
  }

  function reloadForCurrentBranch() {
    if (localStorageSuspended.value || editDraftStorageKey.value) return;

    const newBranchId = localStorage.getItem('selectedBranchId');
    if (newBranchId === currentBranchId.value) return;

    saveToStorage(
      elements.value,
      pageOrientation.value,
      getStorageKey(currentBranchId.value),
      notifyQuotaExceeded
    );

    currentBranchId.value = newBranchId;
    const loaded = loadFromStorage(getStorageKey(newBranchId));
    pageOrientation.value = loaded.orientation;
    const withLogo = refreshHeaderLogoOnLoadedElements(loaded.elements, currentLogoUrl.value);
    elements.value = ensureHeaderExists(withLogo);
    selectedElementId.value = null;
    selectedHeaderPageIndex.value = null;
    history.reset();
  }

  function setLocalStorageSuspended(value: boolean) {
    localStorageSuspended.value = value;
  }

  function setEditDraftStorage(id: number, kind: 'matrix' | 'model' = 'matrix') {
    currentBranchId.value = localStorage.getItem('selectedBranchId');
    editDraftStorageKey.value = getEditStorageKey(id, kind);
    localStorage.removeItem(editDraftStorageKey.value);
    localStorageSuspended.value = true;
  }

  function updateCellContentProperty(
    contentElementId: string,
    propertyUpdates: Record<string, unknown>
  ) {
    for (let i = 0; i < elements.value.length; i++) {
      const element = elements.value[i];
      if (element.type !== 'TABLE') continue;
      const tableProps = element.properties as TableProperties;

      let found = false;
      const newCells = tableProps.cells.map((row) => {
        if (!row.some((cell) => cell.content?.id === contentElementId)) return row;
        return row.map((cell) => {
          if (cell.content?.id === contentElementId) {
            found = true;
            return {
              ...cell,
              content: {
                ...cell.content,
                properties: { ...cell.content.properties, ...propertyUpdates },
              },
            };
          }
          return cell;
        });
      });

      if (found) {
        history.track();
        const updatedTableProperties: TableProperties = {
          ...tableProps,
          cells: newCells,
        };
        elements.value[i] = {
          ...element,
          properties: updatedTableProperties,
        };
        return;
      }
    }
  }

  function reorderElements(newOrder: FormElement[]) {
    history.track();
    elements.value = newOrder;
    updatePositions();
  }

  function undo() {
    return history.undo();
  }

  function redo() {
    return history.redo();
  }

  return {
    elements: readonly(elements),
    pageOrientation,
    selectedElementId: readonly(selectedElementId),
    selectedHeaderPageIndex: readonly(selectedHeaderPageIndex),
    selectedElement,
    hasElements,
    isHeaderModified,
    isHeaderComplete,
    hasEmptyFixedText,
    hasInvalidPropertyEditor,
    setPropertyEditorValidity,
    clearPropertyEditorValidity,
    elementCount,
    formStructure,
    canUndo,
    canRedo,
    addElement,
    removeElement,
    insertElementAt,
    updateElement,
    updateCellContentProperty,
    selectElement,
    moveElement,
    initForCreateMode,
    loadFormStructure,
    appendModelStructure,
    clearCanvas,
    clearDraft,
    reorderElements,
    reloadForCurrentBranch,
    setLocalStorageSuspended,
    setEditDraftStorage,
    undo,
    redo,
  };
});
